import React, { useEffect, useState } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import api from '@/utils/api';
import { showSuccess, showError } from '@/utils/toast';

const LeadEdit = () => {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    fetchData();
  }, [leadId]);

  const fetchData = async () => {
    try {
      const [leadRes, usersRes] = await Promise.all([
        api.get(`/leads/${leadId}`),
        api.get('/users'),
      ]);
      setFormData(leadRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      showError('Failed to load lead');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const submitData = { ...formData };
      ['id', 'created_by', 'created_at', 'updated_at'].forEach((k) => delete submitData[k]);
      if (submitData.loan_amount) submitData.loan_amount = parseFloat(submitData.loan_amount);
      if (submitData.monthly_income) submitData.monthly_income = parseFloat(submitData.monthly_income);
      // Clear co-applicant fields if toggle is off
      if (!submitData.has_co_applicant) {
        submitData.co_applicant_name = null;
        submitData.co_applicant_mobile = null;
        submitData.co_applicant_email = null;
        submitData.co_applicant_relation = null;
      }
      
      await api.put(`/leads/${leadId}`, submitData);
      showSuccess('Lead updated successfully');
      navigate('/leads');
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to update lead');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="animate-spin text-primary" size={40} />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div>
        <button onClick={() => navigate('/leads')} className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors">
          <ArrowLeft size={20} />
          Back to Leads
        </button>

        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">Edit Lead</h1>
          <p className="text-muted-foreground">Update customer information</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-card border border-border rounded-xl p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium mb-2">Customer Name *</label>
              <input type="text" name="customer_name" required value={formData.customer_name || ''} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Mobile Number *</label>
              <input type="tel" name="mobile_number" required value={formData.mobile_number || ''} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Email</label>
              <input type="email" name="email" value={formData.email || ''} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">PAN Number</label>
              <input type="text" name="pan_number" value={formData.pan_number || ''} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">City</label>
              <input type="text" name="city" value={formData.city || ''} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Monthly Income</label>
              <input type="number" name="monthly_income" value={formData.monthly_income || ''} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Loan Type *</label>
              <select name="loan_type" required value={formData.loan_type || ''} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none">
                <option value="Personal Loan">Personal Loan</option>
                <option value="Business Loan">Business Loan</option>
                <option value="Home Loan">Home Loan</option>
                <option value="Auto Loan">Auto Loan</option>
                <option value="Car Loan">Car Loan</option>
                <option value="Education Loan">Education Loan</option>
                <option value="Loan Against Property">Loan Against Property</option>
                <option value="Credit Card">Credit Card</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Loan Amount</label>
              <input type="number" name="loan_amount" value={formData.loan_amount || ''} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Status *</label>
              <select name="status" required data-testid="edit-status" value={formData.status || ''} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none">
                <option value="fresh">Fresh Lead</option>
                <option value="contacted">Contacted</option>
                <option value="interested">Interested</option>
                <option value="documents_pending">Documents Pending</option>
                <option value="follow_up">Follow Up</option>
                <option value="login_done">Login Done</option>
                <option value="under_process">Under Process</option>
                <option value="sanctioned">Sanctioned</option>
                <option value="disbursed">Disbursed</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Assigned To</label>
              <select name="assigned_to" data-testid="assigned-to" value={formData.assigned_to || ''} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none">
                <option value="">-- Unassigned --</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.full_name} ({u.role})</option>
                ))}
              </select>
            </div>
          </div>

          {/* Co-Applicant section */}
          <div className="mt-8 pt-6 border-t border-border" data-testid="edit-co-applicant-block">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h3 className="text-lg font-semibold text-foreground">Co-Applicant</h3>
              <div className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground">Has Co-Applicant?</span>
                <button
                  type="button"
                  data-testid="edit-has-co-applicant-toggle"
                  onClick={() => setFormData((prev) => ({
                    ...prev,
                    has_co_applicant: !prev.has_co_applicant,
                    ...(prev.has_co_applicant ? { co_applicant_name: '', co_applicant_mother_name: '', co_applicant_mobile: '', co_applicant_email: '', co_applicant_relation: '' } : {}),
                  }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${formData.has_co_applicant ? 'bg-primary' : 'bg-muted-foreground/30'}`}
                  role="switch"
                  aria-checked={!!formData.has_co_applicant}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.has_co_applicant ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
                <span className="text-sm font-medium text-foreground">{formData.has_co_applicant ? 'Yes' : 'No'}</span>
              </div>
            </div>
            {formData.has_co_applicant && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Co-Applicant Name</label>
                  <input data-testid="edit-co-applicant-name" type="text" name="co_applicant_name" value={formData.co_applicant_name || ''} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Co-Applicant Mother's Name</label>
                  <input data-testid="edit-co-applicant-mother-name" type="text" name="co_applicant_mother_name" value={formData.co_applicant_mother_name || ''} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Mobile Number</label>
                  <input data-testid="edit-co-applicant-mobile" type="tel" name="co_applicant_mobile" value={formData.co_applicant_mobile || ''} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Email ID</label>
                  <input data-testid="edit-co-applicant-email" type="email" name="co_applicant_email" value={formData.co_applicant_email || ''} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Relationship with Applicant</label>
                  <select data-testid="edit-co-applicant-relation" name="co_applicant_relation" value={formData.co_applicant_relation || ''} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none">
                    <option value="">Select…</option>
                    <option value="Spouse">Spouse</option>
                    <option value="Parent">Parent</option>
                    <option value="Child">Child</option>
                    <option value="Sibling">Sibling</option>
                    <option value="Business Partner">Business Partner</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
            )}
          </div>

          {/* Personal Loan specific fields */}
          {formData.loan_type === 'Personal Loan' && (
            <div className="mt-8 pt-6 border-t border-border" data-testid="edit-personal-loan-fields">
              <h3 className="text-lg font-semibold text-foreground mb-4">Personal Loan — Employment Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Applicant Qualification</label>
                  <input type="text" name="qualification" value={formData.qualification || ''} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Designation</label>
                  <input type="text" name="designation" value={formData.designation || ''} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Current Company Name</label>
                  <input type="text" name="current_company_name" value={formData.current_company_name || ''} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Date of Joining</label>
                  <input type="date" name="date_of_joining" value={formData.date_of_joining || ''} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Current Company Work Experience</label>
                  <input type="text" name="current_company_work_experience" value={formData.current_company_work_experience || ''} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Total Work Experience</label>
                  <input type="text" name="total_work_experience" value={formData.total_work_experience || ''} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">Office Address</label>
                  <textarea rows="2" name="office_address" value={formData.office_address || ''} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">Office Landmark</label>
                  <input type="text" name="office_landmark" value={formData.office_landmark || ''} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none" />
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-4 mt-8">
            <button type="button" onClick={() => navigate('/leads')} className="px-6 py-3 border border-border rounded-lg hover:bg-accent">Cancel</button>
            <button type="submit" disabled={saving} data-testid="update-lead-button" className="flex items-center gap-2 px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground font-medium rounded-lg disabled:opacity-50">
              {saving && <Loader2 className="animate-spin" size={20} />}
              {saving ? 'Updating...' : 'Update Lead'}
            </button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
};

export default LeadEdit;
